
import { CameraInventory, CameraFormValues, cameraSchema } from "@/types/inventory";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, ArrowRight } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AddCameraForm } from "./AddCameraForm";
import { useTranslation } from "@/hooks/useTranslation";

interface CameraCardProps {
  camera: CameraInventory;
  onActionComplete: () => void;
}

export function CameraCard({
  camera,
  onActionComplete
}: CameraCardProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const location = useLocation();
  
  // Check if we're on the camera details page
  const isOnDetailsPage = location.pathname.includes(`/camera/${camera.id}`);

  const form = useForm<CameraFormValues>({
    resolver: zodResolver(cameraSchema),
    defaultValues: {
      brand: camera.brand,
      model: camera.model,
      camera_type: camera.camera_type,
      format: camera.format || null,
      mount: camera.mount || null,
      status: camera.status,
      serial_number: camera.serial_number || "",
      purchase_date: camera.purchase_date || "",
      purchase_price: camera.purchase_price ? camera.purchase_price.toString() : "", 
      notes: camera.notes || "",
      adapter_enabled: camera.adapter_enabled || false,
      adapter_mounts: camera.adapter_mounts || ""
    }
  });

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const query = supabase.from('camera_inventory').delete().eq('id', camera.id);
      const {
        error
      } = await query;
      if (error) throw error;
      toast.success('Camera deleted from inventory');
      setShowDeleteAlert(false);
      onActionComplete();
    } catch (error: any) {
      toast.error(`Error deleting camera: ${error.message}`);
      console.error('Error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdate = async (values: CameraFormValues) => {
    try {
      const query = supabase.from('camera_inventory').update({
        brand: values.brand,
        model: values.model,
        camera_type: values.camera_type,
        format: values.format || null,
        mount: values.mount || null,
        status: values.status,
        serial_number: values.serial_number || null,
        purchase_date: values.purchase_date || null,
        purchase_price: values.purchase_price ? parseFloat(values.purchase_price) : null,
        notes: values.notes || null,
        adapter_enabled: values.adapter_enabled || false,
        adapter_mounts: values.adapter_mounts || null
      }).eq('id', camera.id);
      const {
        error
      } = await query;
      if (error) throw error;
      toast.success('Camera updated');
      setIsEditing(false);
      onActionComplete();
    } catch (error: any) {
      toast.error(`Error updating camera: ${error.message}`);
      console.error('Error:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'working':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
      case 'needs_repair':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100';
      case 'for_parts':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatCameraType = (type: string) => {
    if (type === 'dslr') return 'DSLR';
    if (type === 'slr') return 'SLR';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return <Card className="overflow-hidden hover:shadow-md transition-all duration-300">
      <CardContent className="p-0">
        <Link to={`/camera/${camera.id}`} className="block p-4 hover:bg-muted/30 transition-colors">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="font-medium">{camera.brand} {camera.model}</h3>
              <p className="text-sm text-muted-foreground">
                {formatCameraType(camera.camera_type)}
              </p>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(camera.status)}`}>
              {formatStatus(camera.status)}
            </span>
          </div>
          
          <div className="flex items-center justify-end mt-3 text-primary text-sm">
            <span>{t('view_details')}</span>
            <ArrowRight className="h-3 w-3 ml-1" />
          </div>
        </Link>
        
        <div className="bg-muted/30 p-2 flex justify-end space-x-2 border-t">
          <Dialog open={isEditing} onOpenChange={setIsEditing}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm">
                <Edit className="h-4 w-4 mr-1" />
                {t('edit')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl sm:max-w-4xl md:max-w-4xl w-[95%]">
              <DialogHeader>
                <DialogTitle>{t('edit_camera')}</DialogTitle>
                <DialogDescription>
                  {t('update_camera_details')}
                </DialogDescription>
              </DialogHeader>
              <AddCameraForm form={form} onSubmit={handleUpdate} isEditing={true} />
            </DialogContent>
          </Dialog>
          
          <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setShowDeleteAlert(true)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {t('delete')}
            </Button>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the {camera.brand} {camera.model} camera from your inventory. 
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                <AlertDialogAction 
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : t('delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>;
}
